import { format } from "date-fns";
import { ArrowRight, Brain, Clock } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import { VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import { navigateToViewByType } from "@/other/utils/asyncTaskUtils.jsx";
import { MANIFEST_GUIDE_TARGETS, guideTargetProps } from "@/react/guides";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useActiveBoardContext } from "@/react/components/marketingStack";

function formatSyncedAt(date: Date | null | undefined): string {
  if (!date) return "—";
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "—";
  return format(d, "MMM d, yyyy, hh:mm a");
}

interface ManifestProvenanceBarProps {
  isGenerating: boolean;
  showTopBorder?: boolean;
}

export const ManifestProvenanceBar = ({
  isGenerating,
  showTopBorder = true,
}: ManifestProvenanceBarProps) => {
  const { viewManager } = useSwivuBryntumContext();
  const { activeContext } = useActiveBoardContext();

  const contextLabel = activeContext?.name?.trim() || "Board Context";

  const openContext = () => {
    if (!viewManager) return;
    navigateToViewByType(
      viewManager,
      VIEW_TYPES.context,
      "Context",
      activeContext?.id != null ? { contextId: activeContext.id } : {},
    );
  };

  return (
    <div
      className={cn(
        "shrink-0 flex flex-wrap items-center justify-between gap-x-4 gap-y-0.5 bg-muted/20 px-3 py-1.5 text-[11px] sm:text-xs",
        showTopBorder && "border-t border-border",
      )}
    >
      {activeContext ? (
        <>
          <div
            {...guideTargetProps(MANIFEST_GUIDE_TARGETS.provenance)}
            className="flex items-center gap-2 min-w-0 text-muted-foreground"
          >
            <Brain className="h-3.5 w-3.5 shrink-0" />
            <span className="shrink-0">Created from Context:</span>
            <button
              type="button"
              onClick={openContext}
              className="font-medium text-primary hover:underline truncate"
            >
              {contextLabel}
            </button>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="h-3.5 w-3.5 shrink-0" />
            <span>
              Last synced{" "}
              <span className="font-medium text-foreground">
                {isGenerating ? "now…" : formatSyncedAt(activeContext.activatedAt)}
              </span>
            </span>
          </div>
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-3 min-w-0 w-full justify-between">
          <p className="text-muted-foreground">
            No active Context yet — create one to generate your manifest.
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={openContext}
            {...guideTargetProps(MANIFEST_GUIDE_TARGETS.provenance)}
            className="gap-1.5 shrink-0 h-7 text-xs"
          >
            Go to Context
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};
